import {
  type LogGroup,
  type LogOverviewRow,
  useSetOrderSilencing,
} from '@/api/hooks/useIntegrationLogs'
import type { SagaType } from '@/constants/sagaTypes'
import { GraviButton } from '@gravitate-js/excalibrr'
import { Dropdown, message } from 'antd'
import { useState } from 'react'
import {
  SilencingCheckboxList,
  SilencingPanel,
  useSilenceableSagas,
} from './SilencingCheckboxList'

/**
 * Bulk silencing for the selected rows. Unlike the per-row menu, selections
 * are staged locally and only sent on Save -- one request for the whole
 * selection rather than one per checkbox.
 */
export function BulkSilenceButton({
  selectedRows,
  onDone,
}: { selectedRows: LogOverviewRow[]; onDone: (groups: LogGroup[]) => void }) {
  const sagas = useSilenceableSagas()
  const setSilencing = useSetOrderSilencing()
  const [open, setOpen] = useState(false)
  const [staged, setStaged] = useState<SagaType[]>([])

  // Bulk silencing only applies to order log groups; the endpoint skips any
  // others, so they're dropped here rather than silently counted in.
  const orderRows = selectedRows.filter(
    (row) => row.checkpoints.type === 'OrderIntegrationCheckpoints',
  )

  if (orderRows.length === 0) return null

  const openWith = (next: boolean) => {
    // Seed from the selection when every row already agrees, so opening the
    // dialog on a uniformly-silenced set doesn't read as "nothing silenced".
    if (next) {
      const first = orderRows[0]
      const firstSilenced =
        first && first.checkpoints.type === 'OrderIntegrationCheckpoints'
          ? [...first.checkpoints.silenced_sagas].sort()
          : []
      const uniform = orderRows.every(
        (row) =>
          row.checkpoints.type === 'OrderIntegrationCheckpoints' &&
          [...row.checkpoints.silenced_sagas].sort().join() ===
            firstSilenced.join(),
      )
      setStaged(uniform ? firstSilenced : [])
    }
    setOpen(next)
  }

  const handleSave = () => {
    setSilencing.mutate(
      {
        body: {
          log_group_ids: orderRows.map((row) => row.id),
          sagas_to_silence: staged,
        },
      },
      {
        onSuccess: (groups) => {
          message.success(
            `Silencing updated for ${orderRows.length} order${
              orderRows.length === 1 ? '' : 's'
            }`,
          )
          setOpen(false)
          onDone(groups)
        },
      },
    )
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={openWith}
      trigger={['click']}
      placement='bottomRight'
      dropdownRender={() => (
        <SilencingPanel
          title={`Silence notifications for ${orderRows.length} selected order${
            orderRows.length === 1 ? '' : 's'
          }`}
        >
          <SilencingCheckboxList
            sagas={sagas}
            value={staged}
            onChange={setStaged}
            disabled={setSilencing.isPending}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 10,
            }}
          >
            <GraviButton
              size='small'
              appearance='text'
              onClick={() => setOpen(false)}
            >
              Cancel
            </GraviButton>
            <GraviButton
              size='small'
              color='blue'
              loading={setSilencing.isPending}
              onClick={handleSave}
            >
              Save
            </GraviButton>
          </div>
        </SilencingPanel>
      )}
    >
      <GraviButton size='small' appearance='outline' color='blue'>
        Bulk Silence Orders
      </GraviButton>
    </Dropdown>
  )
}
